/** Ordena filas del reporte Popularidad en pantalla (el API puede devolver otro orden). */

const stripToNumber = (v) => {
  if (v == null || v === '') return NaN;
  if (typeof v === 'number') return Number.isFinite(v) ? v : NaN;
  let s = String(v).trim().replace(/^\((.*)\)$/, '-$1');
  s = s.replace(/[^\d.,-]/g, '');
  if (!s) return NaN;
  const lastComma = s.lastIndexOf(',');
  const lastDot = s.lastIndexOf('.');
  if (lastComma > lastDot) {
    s = s.replace(/\./g, '').replace(',', '.');
  } else {
    s = s.replace(/,/g, '');
  }
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
};

function isTextLikeHeader(header) {
  const n = String(header)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '');
  return /^(nombre|descrip|familia|categoria|producto|articulo|clave|sku|codigo|mesa|mesero|cajero|fecha|hora|turno|tipo|estatus|status|uuid|\bid\b)/i.test(
    n
  );
}

function pickColumn(headers, patterns) {
  for (const h of headers) {
    const key = String(h);
    if (patterns.some((re) => re.test(key))) return key;
  }
  return null;
}

function analyzeNumericColumn(rows, key) {
  const nums = rows.map((r) => stripToNumber(r[key])).filter((n) => Number.isFinite(n));
  if (nums.length < Math.max(1, Math.ceil(rows.length * 0.35))) return null;
  const sum = nums.reduce((a, b) => a + b, 0);
  const max = Math.max(...nums);
  const intLike = nums.filter((n) => Math.abs(n - Math.round(n)) < 1e-4).length / nums.length;
  return { key, sum, max, intLike };
}

function fallbackNumericKey(rows, headers, mayorIngreso) {
  const candidates = [];
  for (const h of headers) {
    if (isTextLikeHeader(h)) continue;
    const a = analyzeNumericColumn(rows, h);
    if (a) candidates.push(a);
  }
  if (!candidates.length) return null;

  if (mayorIngreso) {
    candidates.sort((a, b) => b.sum - a.sum || b.max - a.max);
    return candidates[0].key;
  }

  const byInt = [...candidates].sort((a, b) => b.intLike - a.intLike || b.sum - a.sum);
  const intPick = byInt.find((c) => c.intLike >= 0.45);
  if (intPick) return intPick.key;

  const bySum = [...candidates].sort((a, b) => b.sum - a.sum);
  if (bySum.length >= 2 && bySum[0].sum > bySum[1].sum * 3) {
    return bySum[1].key;
  }
  return bySum[0].key;
}

function resolveSortKey(rows, headers, mayorIngreso, mayorVenta) {
  if (!mayorIngreso && !mayorVenta) return null;

  if (mayorIngreso) {
    const fromName =
      pickColumn(headers, [
        /^importe$/i,
        /^total$/i,
        /importe\s*total/i,
        /total\s*importe/i,
        /ingreso/i,
        /venta\s+total/i,
        /importe\s+venta/i,
        /monto/i,
        /\$\s*total/i,
        /valor\s*venta/i,
      ]) ||
      pickColumn(headers, [/precio/i, /subtotal/i, /\bventa\b/i]);
    if (fromName) return { key: fromName };
  } else {
    const fromName =
      pickColumn(headers, [
        /^cantidad$/i,
        /^cant\.?$/i,
        /cantidad\s*vendida/i,
        /unidades?/i,
        /piezas?/i,
        /num\.?\s*ventas?/i,
        /#\s*ventas?/i,
        /qty/i,
        /volumen/i,
      ]) ||
      pickColumn(headers, [/ventas?\s*\(/i]);
    if (fromName) return { key: fromName };
  }

  const fb = fallbackNumericKey(rows, headers, mayorIngreso);
  return fb ? { key: fb } : null;
}

function firstTextTieBreak(rows, headers) {
  const h = headers.find((x) => isTextLikeHeader(x));
  return h ?? headers[0];
}

/**
 * Popularidad: ordena la tabla de mayor a menor según la columna detectada.
 * - Mayor ingreso: prioriza columnas de importe / total / venta monetaria; si no hay nombre claro,
 *   usa la columna numérica con suma más alta (suelen ser importes).
 * - Mayor venta: prioriza cantidad / unidades; si no, la columna más “entera” o la segunda por suma
 *   (evita reutilizar solo el importe).
 */
export function sortPopularidadRows(rows, headers, mayorIngreso, mayorVenta) {
  if (!rows?.length || (!mayorIngreso && !mayorVenta)) return rows;

  const resolved = resolveSortKey(rows, headers, mayorIngreso, mayorVenta);
  if (!resolved?.key) return rows;

  const tb = firstTextTieBreak(rows, headers);

  return [...rows].sort((a, b) => {
    const na = stripToNumber(a[resolved.key]);
    const nb = stripToNumber(b[resolved.key]);
    const fa = Number.isFinite(na) ? na : 0;
    const fb = Number.isFinite(nb) ? nb : 0;
    if (fb !== fa) return fb - fa;
    if (tb) {
      const sa = String(a[tb] ?? '');
      const sb = String(b[tb] ?? '');
      const c = sa.localeCompare(sb, 'es', { sensitivity: 'base' });
      if (c !== 0) return c;
    }
    return 0;
  });
}
